import React, { useState, useEffect } from "react";
import { api, useBusiness } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATE_OPTIONS = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "District of Columbia" }
];

const ENTITY_TYPES = [
  { value: "sole_proprietor", label: "Sole Proprietor", description: "Simplest structure, income on Schedule C" },
  { value: "llc", label: "Single-Member LLC", description: "Limited liability, taxed as sole proprietor by default" },
  { value: "s_corp", label: "S Corporation", description: "Pass-through taxation, potential SE tax savings" },
  { value: "c_corp", label: "C Corporation", description: "Separate entity, double taxation" }
];

const TaxPlans = () => {
  const { selectedBusiness } = useBusiness();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("create");
  const [comparison, setComparison] = useState(null);
  const [comparingEntities, setComparingEntities] = useState(false);
  
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    entity_type: "sole_proprietor",
    estimated_annual_income: "",
    estimated_deductions: "",
    state: "",
    notes: ""
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchPlans();
    }
  }, [selectedBusiness]);

  const fetchPlans = async () => {
    if (!selectedBusiness) return;
    try {
      const response = await api.get(`/tax-plans/${selectedBusiness.id}`);
      setPlans(response.data);
    } catch (error) {
      console.error("Failed to fetch tax plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) {
      toast.error("Please select a business first");
      return;
    }

    if (!formData.estimated_annual_income || !formData.state) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      const response = await api.post("/tax-plans", {
        business_id: selectedBusiness.id,
        year: parseInt(formData.year),
        entity_type: formData.entity_type,
        estimated_annual_income: parseFloat(formData.estimated_annual_income),
        estimated_deductions: parseFloat(formData.estimated_deductions) || 0,
        state: formData.state,
        notes: formData.notes || null
      });
      
      setPlans([response.data, ...plans.filter(p => p.year !== response.data.year)]);
      toast.success("Tax plan created successfully!");
      setActiveTab("plans");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tax plan");
    } finally {
      setSaving(false);
    }
  };

  const compareEntityTypes = async () => {
    if (!selectedBusiness || !formData.estimated_annual_income || !formData.state) {
      toast.error("Please enter income and state first");
      return;
    }

    setComparingEntities(true);
    try {
      const response = await api.get(`/tax-plans/compare-entities/${selectedBusiness.id}`, {
        params: {
          annual_income: parseFloat(formData.estimated_annual_income),
          state: formData.state
        }
      });
      setComparison(response.data);
    } catch (error) {
      toast.error("Failed to compare entity types");
    } finally {
      setComparingEntities(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!selectedBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-stone-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-stone-900 mb-2">No Business Selected</h3>
        <p className="text-stone-600">Please select or create a business to create tax plans.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tax-plans-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Strategic Tax Planning</h1>
        <p className="text-stone-600">Plan your taxes, compare entity types, and maximize deductions for {selectedBusiness.name}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="create">Create Plan</TabsTrigger>
          <TabsTrigger value="compare">Compare Entities</TabsTrigger>
          <TabsTrigger value="plans">Your Plans</TabsTrigger>
        </TabsList>

        {/* Create Plan Tab */}
        <TabsContent value="create" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create Tax Plan for {formData.year}</CardTitle>
              <CardDescription>
                Enter your estimated income and deductions to get quarterly payment estimates and tax-saving recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="year">Tax Year</Label>
                    <Select value={formData.year.toString()} onValueChange={(v) => setFormData({...formData, year: parseInt(v)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="entity_type">Entity Type</Label>
                    <Select value={formData.entity_type} onValueChange={(v) => setFormData({...formData, entity_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENTITY_TYPES.map(entity => (
                          <SelectItem key={entity.value} value={entity.value}>
                            {entity.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {ENTITY_TYPES.find(e => e.value === formData.entity_type)?.description}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="income">Estimated Annual Income *</Label>
                    <Input
                      id="income"
                      type="number"
                      placeholder="100000"
                      value={formData.estimated_annual_income}
                      onChange={(e) => setFormData({...formData, estimated_annual_income: e.target.value})}
                      required
                    />
                    <p className="text-xs text-muted-foreground">Gross business income before deductions</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="deductions">Estimated Deductions</Label>
                    <Input
                      id="deductions"
                      type="number"
                      placeholder="20000"
                      value={formData.estimated_deductions}
                      onChange={(e) => setFormData({...formData, estimated_deductions: e.target.value})}
                    />
                    <p className="text-xs text-muted-foreground">Total business deductions (use Deductions Library for ideas)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Select value={formData.state} onValueChange={(v) => setFormData({...formData, state: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {STATE_OPTIONS.map(state => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any notes about this tax plan..."
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={3}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]">
                    {saving ? "Creating..." : "Create Tax Plan"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={compareEntityTypes}
                    disabled={comparingEntities}
                  >
                    {comparingEntities ? "Comparing..." : "Compare All Entity Types"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compare Entities Tab */}
        <TabsContent value="compare" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Entity Type Comparison</CardTitle>
              <CardDescription>
                Compare tax implications across different business structures
              </CardDescription>
            </CardHeader>
            <CardContent>
              {comparison ? (
                <div className="space-y-6">
                  <div className="p-4 rounded-lg bg-stone-50">
                    <p className="text-sm text-stone-600">
                      Comparing for <strong>{formatCurrency(comparison.annual_income)}</strong> annual income in <strong>{comparison.state_name}</strong>
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {comparison.comparisons.map((comp, idx) => (
                      <Card key={comp.entity_type} className={idx === 0 ? "border-[#1e3a5f] border-2" : ""}>
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">{comp.name}</CardTitle>
                            {idx === 0 && <Badge className="bg-[#1e3a5f]">Recommended</Badge>}
                          </div>
                          <CardDescription className="text-xs">{comp.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="p-2 rounded bg-stone-50">
                              <p className="text-xs text-stone-500">Federal Tax</p>
                              <p className="font-semibold">{formatCurrency(comp.federal_tax)}</p>
                            </div>
                            <div className="p-2 rounded bg-stone-50">
                              <p className="text-xs text-stone-500">State Tax</p>
                              <p className="font-semibold">{formatCurrency(comp.state_tax)}</p>
                            </div>
                            <div className="p-2 rounded bg-stone-50">
                              <p className="text-xs text-stone-500">SE Tax</p>
                              <p className="font-semibold">{formatCurrency(comp.self_employment_tax)}</p>
                            </div>
                            <div className="p-2 rounded bg-[#1e3a5f]/5">
                              <p className="text-xs text-[#1e3a5f]">Total Tax</p>
                              <p className="font-bold text-[#1e3a5f]">{formatCurrency(comp.total_tax)}</p>
                            </div>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-stone-600">Effective Rate</span>
                            <Badge variant="outline">{comp.effective_rate}%</Badge>
                          </div>
                          {comp.annual_savings_vs_sole_prop !== 0 && (
                            <div className={`p-2 rounded text-center ${comp.annual_savings_vs_sole_prop > 0 ? 'bg-[#1e3a5f]/10 text-[#1e3a5f]' : 'bg-red-100 text-red-700'}`}>
                              {comp.annual_savings_vs_sole_prop > 0 ? 'Save' : 'Pay'} {formatCurrency(Math.abs(comp.annual_savings_vs_sole_prop))}/year vs Sole Prop
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">
                    Enter your income and state in the "Create Plan" tab, then click "Compare All Entity Types"
                  </p>
                  <Button variant="outline" onClick={() => setActiveTab("create")}>
                    Go to Create Plan
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading plans...</div>
          ) : plans.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground mb-4">No tax plans created yet.</p>
                <Button variant="outline" onClick={() => setActiveTab("create")}>
                  Create Your First Plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            plans.map(plan => (
              <Card key={plan.id} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-[#1e3a5f]/5 to-stone-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{plan.year} Tax Plan</CardTitle>
                      <CardDescription>
                        {ENTITY_TYPES.find(e => e.value === plan.entity_type)?.label} • {STATE_OPTIONS.find(s => s.code === plan.state)?.name}
                      </CardDescription>
                    </div>
                    <Badge className="bg-[#1e3a5f]">{plan.effective_rate}% Effective Rate</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-stone-50 text-center">
                      <p className="text-xs text-stone-500 mb-1">Gross Income</p>
                      <p className="text-xl font-bold text-stone-900">{formatCurrency(plan.estimated_annual_income)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-stone-50 text-center">
                      <p className="text-xs text-stone-500 mb-1">Deductions</p>
                      <p className="text-xl font-bold text-stone-900">{formatCurrency(plan.estimated_deductions)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-stone-50 text-center">
                      <p className="text-xs text-stone-500 mb-1">Taxable Income</p>
                      <p className="text-xl font-bold text-stone-900">{formatCurrency(plan.taxable_income)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-red-50 text-center">
                      <p className="text-xs text-red-500 mb-1">Total Tax</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(plan.total_tax)}</p>
                    </div>
                  </div>

                  {/* Tax Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg border">
                      <p className="text-sm text-stone-500 mb-1">Federal Income Tax</p>
                      <p className="text-2xl font-bold text-stone-900">{formatCurrency(plan.federal_tax)}</p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <p className="text-sm text-stone-500 mb-1">State Income Tax</p>
                      <p className="text-2xl font-bold text-stone-900">{formatCurrency(plan.state_tax)}</p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <p className="text-sm text-stone-500 mb-1">Self-Employment Tax</p>
                      <p className="text-2xl font-bold text-stone-900">{formatCurrency(plan.self_employment_tax)}</p>
                    </div>
                  </div>

                  {/* Quarterly Estimates */}
                  <div>
                    <h4 className="font-semibold text-stone-900 mb-3">Quarterly Estimated Payments (Form 1040-ES)</h4>
                    <div className="grid grid-cols-4 gap-4">
                      {["Q1", "Q2", "Q3", "Q4"].map((q, idx) => (
                        <div key={q} className="p-4 rounded-lg bg-blue-50 text-center">
                          <p className="text-xs text-blue-600 mb-1">{q} ({["Apr 15", "Jun 15", "Sep 15", "Jan 15"][idx]})</p>
                          <p className="text-lg font-bold text-blue-700">{formatCurrency(plan[`q${idx+1}_estimate`])}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h4 className="font-semibold text-stone-900 mb-3">Tax-Saving Recommendations</h4>
                    <div className="space-y-2">
                      {plan.recommendations?.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-[#1e3a5f]/5">
                          <div className="p-1 rounded-full bg-[#1e3a5f]/10 mt-0.5">
                            <svg className="w-4 h-4 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="text-sm text-[#1e3a5f]">{rec}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {plan.notes && (
                    <div className="p-4 rounded-lg bg-stone-50">
                      <p className="text-xs text-stone-500 mb-1">Notes</p>
                      <p className="text-sm text-stone-700">{plan.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="bg-gradient-to-br from-blue-50 to-stone-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-800 flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            Important Tax Dates (2024)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-white">
              <p className="font-semibold text-blue-800">Q1 Estimate</p>
              <p className="text-sm text-blue-600">April 15</p>
            </div>
            <div className="p-3 rounded-lg bg-white">
              <p className="font-semibold text-blue-800">Q2 Estimate</p>
              <p className="text-sm text-blue-600">June 17</p>
            </div>
            <div className="p-3 rounded-lg bg-white">
              <p className="font-semibold text-blue-800">Q3 Estimate</p>
              <p className="text-sm text-blue-600">September 16</p>
            </div>
            <div className="p-3 rounded-lg bg-white">
              <p className="font-semibold text-blue-800">Q4 Estimate</p>
              <p className="text-sm text-blue-600">January 15 (2025)</p>
            </div>
          </div>
          <div className="mt-4 p-4 rounded-lg bg-amber-100 text-sm text-amber-800">
            <strong>Disclaimer:</strong> This tool provides estimates for planning purposes only. Tax laws are complex and change frequently. 
            Always consult with a qualified CPA or tax professional for personalized advice.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxPlans;
